import { useCallback, useEffect, useRef, useState } from 'react';
import type { JobType } from '@xphoto/shared';
import { useJobActivity } from './use-job-activity';
import { hasActiveJobOfType } from './job-activity';

/**
 * Auto-rafraîchissement d'une liste APRÈS la fin d'un job de fond (miroir du patron
 * `useExifAnalysis` § « Auto-rafraîchissement à la fin d'un job `exif_erase` »).
 *
 * Problème résolu : une action (ex. suppression de copies exactes) enfile un job
 * ASYNCHRONE (`dedup_exact_apply` / `dedup_apply`) puis rechargeait IMMÉDIATEMENT la
 * liste — mais le job n'a pas encore supprimé les fichiers, le serveur renvoie donc
 * les MÊMES chemins (`is_missing=false`) → les mêmes images sont re-proposées à la
 * suppression. Il faut attendre la FIN du job pour recharger.
 *
 * Ce hook observe l'activité des jobs (cloche : SEED + SSE) et déclenche `onFinished`
 * au bon moment :
 *  - FRONT DESCENDANT : un job du type a été observé actif, puis ne l'est plus → fin
 *    normale, on recharge.
 *  - REPLI PAR DÉLAI (`JOB_APPEAR_GRACE_MS`) : si le job se termine si vite qu'on ne
 *    l'a JAMAIS vu actif (fenêtre enfilage→SEED/SSE), le délai déclenche le rechargement.
 *    ⚠ Si on l'a bien vu actif, on NE force PAS au délai (une grosse suppression peut
 *    dépasser le délai de grâce) — on s'en remet alors au front descendant.
 *
 * `pending` reste vrai depuis `markPending()` jusqu'au déclenchement de `onFinished`.
 * L'appelant désactive son bouton d'action tant que `pending || active` (anti double-submit).
 */

/**
 * Délai de grâce (ms) après `markPending()` : si le job ne s'est JAMAIS manifesté comme
 * actif dans cette fenêtre (terminé trop vite pour être observé), on recharge quand même.
 * Aligné sur l'esprit de `SCAN_APPEAR_GRACE_MS` (verrou de scan, v0.20.0), un peu plus
 * large car une suppression peut mettre du temps à APPARAÎTRE (latence SEED/SSE).
 */
export const JOB_APPEAR_GRACE_MS = 12_000;

export interface UseRefreshAfterJob {
  /** Un job du type observé est actuellement actif (queued/running). */
  active: boolean;
  /** Une action a été enfilée et on attend encore la fin du job. */
  pending: boolean;
  /** À appeler juste après l'enfilage réussi du job → arme l'observation. */
  markPending: () => void;
}

export function useRefreshAfterJob(
  libraryId: string,
  jobType: JobType,
  onFinished: () => void,
): UseRefreshAfterJob {
  const activity = useJobActivity(libraryId);
  const active = hasActiveJobOfType(activity, jobType);

  const [pending, setPending] = useState(false);

  // Refs pour lire l'état courant dans des callbacks/effets sans re-armer les effets.
  const prevActiveRef = useRef(active);
  const sawActiveRef = useRef(false);
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // `onFinished` peut changer d'identité à chaque rendu (closure de l'appelant) : on lit
  // toujours la dernière version via une ref → aucun effet à re-armer pour autant.
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  const clearGraceTimer = useCallback(() => {
    if (graceTimerRef.current !== null) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }
  }, []);

  // Fin du job → recharge une fois, désarme.
  const finish = useCallback(() => {
    clearGraceTimer();
    sawActiveRef.current = false;
    setPending(false);
    onFinishedRef.current();
  }, [clearGraceTimer]);

  const markPending = useCallback(() => {
    sawActiveRef.current = false;
    setPending(true);
    clearGraceTimer();
    // Repli : si le job ne se manifeste jamais comme actif, on recharge après le délai.
    graceTimerRef.current = setTimeout(() => {
      graceTimerRef.current = null;
      // On ne force le rechargement au délai QUE si le job n'a jamais été vu actif.
      // Sinon (job long, encore en cours) : le front descendant s'en chargera.
      if (!sawActiveRef.current) finish();
    }, JOB_APPEAR_GRACE_MS);
  }, [clearGraceTimer, finish]);

  // Observe l'activité : mémorise « vu actif » et détecte le front descendant.
  useEffect(() => {
    if (active) sawActiveRef.current = true;
    const wasActive = prevActiveRef.current;
    prevActiveRef.current = active;
    if (pending && wasActive && !active) {
      // Un job du type vient de se terminer → recharge.
      finish();
    }
  }, [active, pending, finish]);

  // Nettoyage du minuteur au démontage.
  useEffect(() => clearGraceTimer, [clearGraceTimer]);

  return { active, pending, markPending };
}
